import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, Target, 
  TrendingUp, Users, DollarSign, Clock, Brain, Sparkles,
  Download, Share2, Calendar, Rocket, AlertCircle, ChevronRight,
  Star, Check, Zap, Award, Mail, Copy
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { trackPageView, trackEvent, trackConversion } from "@/utils/analytics";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Assessment Questions organized by category
const ASSESSMENT_SECTIONS = [
  {
    id: "business_basics",
    title: "About You & Your Brand",
    icon: Target,
    questions: [
      {
        id: "business_name",
        type: "text",
        question: "What's your business or brand name?",
        placeholder: "Enter your business, brand, or stage name",
        required: true
      },
      {
        id: "profile_type",
        type: "radio",
        question: "Which best describes you?",
        options: [
          { value: "entrepreneur", label: "Entrepreneur / Business Owner" },
          { value: "entertainer", label: "Entertainer / Artist / Performer" },
          { value: "influencer", label: "Influencer / Content Creator" },
          { value: "hospitality", label: "Hospitality / Restaurant / Venue / Event Space" },
          { value: "hybrid", label: "Combination — I wear multiple hats" }
        ],
        required: true
      },
      {
        id: "business_stage",
        type: "radio",
        question: "What stage are you currently in?",
        options: [
          { value: "idea", label: "Idea Stage — still planning my next move" },
          { value: "startup", label: "Early Stage — less than 1 year building" },
          { value: "growing", label: "Growing — 1-3 years, gaining traction" },
          { value: "established", label: "Established — 3+ years, consistent income" },
          { value: "scaling", label: "Scaling — ready to expand significantly" }
        ],
        required: true
      },
      {
        id: "industry",
        type: "text",
        question: "What's your industry, niche, or genre?",
        placeholder: "e.g., Music, Restaurant, Fitness, Fashion, Marketing, Nightlife, Events, Beauty, Tech",
        required: true
      }
    ]
  },
  {
    id: "revenue_goals",
    title: "Revenue & Goals",
    icon: DollarSign,
    questions: [
      {
        id: "current_revenue",
        type: "radio",
        question: "What's your current monthly income from your brand/business?",
        options: [
          { value: "pre_revenue", label: "Pre-revenue (not yet earning)" },
          { value: "under_5k", label: "Under $5,000/month" },
          { value: "5k_10k", label: "$5,000 - $10,000/month" },
          { value: "10k_25k", label: "$10,000 - $25,000/month" },
          { value: "25k_50k", label: "$25,000 - $50,000/month" },
          { value: "over_50k", label: "Over $50,000/month" }
        ],
        required: true
      },
      {
        id: "revenue_goal",
        type: "radio",
        question: "What's your income goal for the next 12 months?",
        options: [
          { value: "first_revenue", label: "Generate my first consistent income" },
          { value: "double", label: "Double my current income" },
          { value: "10k_month", label: "Reach $10,000/month" },
          { value: "25k_month", label: "Reach $25,000/month" },
          { value: "50k_month", label: "Reach $50,000/month" },
          { value: "100k_plus", label: "Reach $100,000+/month" }
        ],
        required: true
      }
    ]
  },
  {
    id: "challenges",
    title: "Current Challenges",
    icon: AlertCircle,
    questions: [
      {
        id: "biggest_challenge",
        type: "radio",
        question: "What's your biggest challenge right now?",
        options: [
          { value: "clarity", label: "Lack of clarity on what to focus on" },
          { value: "leads", label: "Not enough leads, fans, customers, or foot traffic" },
          { value: "sales", label: "Difficulty closing deals or monetizing" },
          { value: "time", label: "Not enough time — doing everything myself" },
          { value: "systems", label: "No systems or processes in place" },
          { value: "scaling", label: "Can't scale beyond my current level" },
          { value: "brand", label: "Need to strengthen my brand or positioning" },
          { value: "marketing", label: "Marketing isn't driving results — need a strategy" },
          { value: "burnout", label: "Feeling overwhelmed or burned out" }
        ],
        required: true
      },
      {
        id: "time_spent",
        type: "radio",
        question: "How much time do you spend working ON your brand/business (strategy) vs IN it (daily tasks)?",
        options: [
          { value: "all_in", label: "100% in the grind — no time for strategy" },
          { value: "mostly_in", label: "80% grinding, 20% planning" },
          { value: "balanced", label: "50/50 — balanced approach" },
          { value: "mostly_on", label: "20% tasks, 80% strategy — focused on growth" }
        ],
        required: true
      },
      {
        id: "challenge_details",
        type: "textarea",
        question: "Describe your current situation. What's holding you back from the next level?",
        placeholder: "Share what's been on your mind about your business, brand, or career...",
        required: true
      }
    ]
  },
  {
    id: "vision",
    title: "Your Vision",
    icon: Rocket,
    questions: [
      {
        id: "ideal_outcome",
        type: "textarea",
        question: "If we could solve your biggest challenge, what would your business/brand look like in 90 days?",
        placeholder: "Describe your ideal outcome — more income, bigger audience, full venues, better systems, more freedom...",
        required: true
      },
      {
        id: "support_needed",
        type: "radio",
        question: "What type of support would be most valuable to you?",
        options: [
          { value: "strategy", label: "Strategic guidance and planning" },
          { value: "marketing", label: "Marketing, branding, and audience growth" },
          { value: "hospitality_marketing", label: "Hospitality marketing — fill seats, drive sales, build loyalty" },
          { value: "systems", label: "Help building systems and automations" },
          { value: "monetization", label: "Monetization and revenue strategy" },
          { value: "accountability", label: "Accountability and regular check-ins" },
          { value: "all", label: "All of the above" }
        ],
        required: true
      },
      {
        id: "name",
        type: "text",
        question: "What's your name?",
        placeholder: "Your first name",
        required: true
      },
      {
        id: "email",
        type: "email",
        question: "What's your email? (We'll send your results here)",
        placeholder: "your@email.com",
        required: false
      }
    ]
  }
];

export const ClarityAssessment = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  // Post-assessment reveal flow state
  const [revealStep, setRevealStep] = useState(0); // 0: results, 1: insights, 2: recommendation, 3: pricing
  
  // Email/Download state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    trackPageView('/clarity-assessment', 'Business Clarity Assessment');
  }, []);

  // Generate PDF-style report text
  const generateReportText = () => {
    if (!results) return "";
    
    let report = `
═══════════════════════════════════════════════════════════════
                 BUSINESS CLARITY REPORT
                   My Biz Consulting
═══════════════════════════════════════════════════════════════

Prepared for: ${answers.name || 'Client'}
Business/Brand: ${answers.business_name || 'N/A'}
Profile: ${answers.profile_type === 'entrepreneur' ? 'Entrepreneur' : answers.profile_type === 'entertainer' ? 'Entertainer / Artist' : answers.profile_type === 'influencer' ? 'Influencer / Creator' : answers.profile_type === 'hospitality' ? 'Hospitality / Venue' : 'Multi-Hyphenate'}
Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}

───────────────────────────────────────────────────────────────
CLARITY SCORE: ${results.clarity_score}/100
───────────────────────────────────────────────────────────────

${results.clarity_score >= 70 ? '✓ Strong Foundation - Ready to scale with the right support' : 
  results.clarity_score >= 40 ? '⚠ Growth Potential - Key areas need attention' : 
  '⚡ Critical Focus Needed - Foundational work required'}

───────────────────────────────────────────────────────────────
TOP GROWTH BLOCKERS IDENTIFIED
───────────────────────────────────────────────────────────────

${results.priorities?.map((p, i) => `${i + 1}. ${p}`).join('\n') || 'No blockers identified'}

───────────────────────────────────────────────────────────────
KEY INSIGHTS
───────────────────────────────────────────────────────────────

${results.insights?.map((insight, i) => `• ${insight}`).join('\n') || 'No insights available'}

───────────────────────────────────────────────────────────────
RECOMMENDATIONS
───────────────────────────────────────────────────────────────

${results.recommendations?.map((rec, i) => `${i + 1}. ${rec}`).join('\n') || 'No recommendations available'}
`;

    // Add 30-day plan if available
    if (results.thirty_day_plan) {
      report += `
───────────────────────────────────────────────────────────────
YOUR 30-DAY ACTION PLAN
───────────────────────────────────────────────────────────────

${results.thirty_day_plan.overview || ''}

`;
      ['week_1', 'week_2', 'week_3', 'week_4'].forEach((weekKey, idx) => {
        const week = results.thirty_day_plan[weekKey];
        if (week) {
          report += `
WEEK ${idx + 1}: ${week.title || ''}
Focus: ${week.focus || ''}
Tasks:
${week.tasks?.map(t => `  • ${t}`).join('\n') || '  • No tasks'}
Outcome: ${week.outcome || ''}
`;
        }
      });
    }

    report += `
───────────────────────────────────────────────────────────────
NEXT STEPS
───────────────────────────────────────────────────────────────

Ready to accelerate your growth? Here's how we can help:

1. Book a Strategy Call: Discuss your results with a consultant
2. Explore Our Programs: Visit mybizconsulting.net/pricing
3. Join Our Community: Connect with other entrepreneurs

───────────────────────────────────────────────────────────────

Questions? Contact us at support@mybizconsulting.net

© ${new Date().getFullYear()} My Biz Consulting
═══════════════════════════════════════════════════════════════
`;

    return report;
  };

  // Download report as text file
  const handleDownloadReport = () => {
    setDownloading(true);
    try {
      const report = generateReportText();
      const blob = new Blob([report], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Business-Clarity-Report-${answers.name || 'Report'}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
      trackEvent('clarity_report_download', { name: answers.name });
    } catch (err) {
      toast.error("Failed to download report");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  // Email report to user
  const handleEmailReport = async () => {
    const email = answers.email;
    if (!email) {
      toast.error("Please provide your email to receive the report");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await fetch(`${API}/api/assessment/email-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          name: answers.name || 'Business Owner',
          business_name: answers.business_name,
          results: results
        })
      });
      
      if (response.ok) {
        toast.success(`Report sent to ${email}!`);
        trackEvent('clarity_report_email', { email });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send report");
      console.error(err);
    } finally {
      setSendingEmail(false);
    }
  };

  // Copy report to clipboard
  const handleCopyReport = async () => {
    try {
      const report = generateReportText();
      await navigator.clipboard.writeText(report);
      toast.success("Report copied to clipboard!");
      trackEvent('clarity_report_copy', { name: answers.name });
    } catch (err) {
      toast.error("Failed to copy report");
    }
  };

  const totalSections = ASSESSMENT_SECTIONS.length;
  const progress = ((currentSection + 1) / (totalSections + 1)) * 100; // +1 for results

  const currentSectionData = ASSESSMENT_SECTIONS[currentSection];

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isCurrentSectionComplete = () => {
    if (!currentSectionData) return true;
    return currentSectionData.questions.every(q => {
      if (!q.required) return true;
      const answer = answers[q.id];
      return answer && answer.toString().trim() !== '';
    });
  };

  const handleNext = () => {
    if (currentSection < totalSections - 1) {
      setCurrentSection(prev => prev + 1);
      trackEvent('assessment_section_complete', { section: currentSectionData.id });
    } else {
      submitAssessment();
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  };

  const submitAssessment = async () => {
    setLoading(true);
    setError(null);
    trackEvent('assessment_submit_start', { answers_count: Object.keys(answers).length });

    try {
      const response = await fetch(`${API}/api/assessment/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        trackConversion('assessment_complete', { 
          business_stage: answers.business_stage,
          revenue_level: answers.current_revenue 
        });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to analyze assessment');
        trackEvent('assessment_error', { error: errorData.detail });
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
      trackEvent('assessment_error', { error: 'network_error' });
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question) => {
    const value = answers[question.id] || '';

    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <div key={question.id} className="space-y-3">
            <Label className="text-base font-medium text-neutral-900">
              {question.question}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type={question.type}
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              className="h-12 text-base"
              data-testid={`assessment-${question.id}`}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={question.id} className="space-y-3">
            <Label className="text-base font-medium text-neutral-900">
              {question.question}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              className="min-h-[120px] text-base"
              data-testid={`assessment-${question.id}`}
            />
          </div>
        );

      case 'radio':
        return (
          <div key={question.id} className="space-y-3">
            <Label className="text-base font-medium text-neutral-900">
              {question.question}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={value}
              onValueChange={(val) => handleInputChange(question.id, val)}
              className="space-y-2"
            >
              {question.options.map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                    value === option.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                  onClick={() => handleInputChange(question.id, option.value)}
                >
                  <RadioGroupItem value={option.value} id={`${question.id}-${option.value}`} />
                  <Label
                    htmlFor={`${question.id}-${option.value}`}
                    className="flex-1 cursor-pointer text-sm"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      default:
        return null;
    }
  };

  // Determine recommended path based on answers
  const getRecommendedPath = () => {
    const stage = answers.business_stage;
    const challenge = answers.biggest_challenge;
    const revenue = answers.current_revenue;
    
    // Leadership/Scale path - for scaling businesses or those managing teams
    if (stage === 'scaling' || challenge === 'scaling') {
      return 'scale';
    }
    // Execution/Growth path - for established businesses wanting optimization
    if (stage === 'established' || stage === 'growing' || 
        ['10k_25k', '25k_50k', 'over_50k'].includes(revenue)) {
      return 'growth';
    }
    // Clarity path - for earlier stage or those needing clarity
    return 'clarity';
  };

  const RECOMMENDED_PATHS = {
    clarity: {
      title: "R.A.P.I.D.™ Business Recalibration",
      subtitle: "Reset priorities, clarify decisions, and regain momentum",
      price: "$1,250–$2,500",
      type: "One-Time Investment",
      description: "Based on your assessment, you would benefit most from our proprietary R.A.P.I.D.™ framework. This intensive program helps you reset your priorities, clarify decisions, and build a concrete 90-day momentum plan.",
      successLooks: "Clear priorities, organized systems, and a strategic roadmap you can execute with confidence.",
      features: [
        "Strategic business assessment",
        "Structured recalibration sessions",
        "30-60-90 day momentum plan",
        "AI-assisted strategy tools",
        "Certificate of completion"
      ],
      cta: "Apply to Begin R.A.P.I.D.™",
      secondaryCta: "Start With Free Community",
      icon: Zap
    },
    growth: {
      title: "Growth & Optimization Retainer",
      subtitle: "Turn strategy into measurable momentum",
      price: "$1,500/month",
      type: "3-6 Month Engagement",
      popular: true,
      description: "Based on your assessment, the Growth & Optimization Retainer is the best fit for your current stage. You're ready to turn strategy into measurable momentum with deeper execution support.",
      successLooks: "Optimized systems, improved revenue, clear KPIs, and consistent growth trajectory within 90 days.",
      features: [
        "Bi-weekly strategy & execution sessions",
        "Priority consultant access",
        "Advanced analytics & performance tracking",
        "Marketing & offer optimization",
        "Competitor & positioning analysis",
        "AI-assisted implementation tools",
        "Accountability tracking"
      ],
      cta: "Start Growth & Optimization",
      secondaryCta: "Start With R.A.P.I.D.™ First",
      icon: TrendingUp
    },
    scale: {
      title: "Scale & Leadership Retainer",
      subtitle: "Strategic partnership for intentional scaling",
      price: "$3,000–$5,000/month",
      type: "Application Only",
      exclusive: true,
      description: "Based on your assessment, you're ready for a strategic partnership. The Scale & Leadership Retainer provides the high-level advisory support you need to scale intentionally and manage complexity.",
      successLooks: "Clear delegation, operational systems, team alignment, and sustainable growth infrastructure.",
      features: [
        "Weekly strategic advisory sessions",
        "Full business operating system dashboard",
        "Custom AI workflows & SOPs",
        "Team planning & leadership support",
        "Quarterly strategic reviews",
        "High-level decision support"
      ],
      cta: "Apply for Strategic Scaling",
      secondaryCta: "Schedule a Clarity Call",
      icon: Rocket
    }
  };

  // Results View with Multi-Step Reveal Flow
  if (results) {
    const recommendedPath = RECOMMENDED_PATHS[getRecommendedPath()];
    const PathIcon = recommendedPath.icon;
    
    // Step 0: Results Ready Screen
    if (revealStep === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#39FF14]/20 rounded-full mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#39FF14]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-neutral-900 mb-4">
              Your Business Clarity Results Are Ready
            </h1>
            <p className="text-lg text-neutral-600 mb-8">
              Based on your responses, we&apos;ve identified your key bottlenecks and most effective next step.
            </p>
            <Button 
              size="lg"
              onClick={() => {
                trackEvent('assessment_reveal_step', { step: 'view_results' });
                setRevealStep(1);
              }}
              className="bg-[#39FF14] hover:bg-[#32E010] text-black font-medium h-14 px-8"
              data-testid="view-results-btn"
            >
              View My Results
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      );
    }
    
    // Step 1: Insight Summary (No Pricing Yet)
    if (revealStep === 1) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-12 px-4">
          <div className="max-w-3xl mx-auto">
            {/* Header with Score */}
            <div className="text-center mb-8">
              <p className="text-neutral-500 mb-2">Hi {answers.name || 'there'}!</p>
              <h1 className="text-3xl font-semibold text-neutral-900 mb-4">
                Your Business Clarity Report
              </h1>
              <div className="inline-flex items-center gap-4 px-6 py-3 bg-[#39FF14]/10 rounded-full">
                <span className="text-sm text-neutral-600">Clarity Score:</span>
                <span className="text-2xl font-bold text-[#39FF14]">{results.clarity_score}/100</span>
              </div>
            </div>

            {/* Key Blockers */}
            <Card className="mb-6 border-red-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  Top Growth Blockers Identified
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.priorities?.slice(0, 3).map((priority, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-neutral-700">{priority}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Key Opportunity */}
            <Card className="mb-6 border-[#39FF14]/30 bg-[#39FF14]/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Key Opportunity</h3>
                    <p className="text-neutral-700">{results.insights?.[0]}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Warning */}
            <Card className="mb-8 border-amber-200 bg-amber-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">If Nothing Changes...</h3>
                    <p className="text-amber-800">
                      Without strategic clarity and proper systems, these bottlenecks will continue to limit your growth, 
                      drain your time, and prevent you from reaching your revenue goals.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom Business Development Proposal */}
            {results.proposal && (
              <div className="mb-8 space-y-4" data-testid="custom-proposal">
                {/* Proposal Header */}
                <div className="bg-[#333333] rounded-2xl p-6 text-center text-white">
                  <p className="text-[#39FF14] text-xs font-medium uppercase tracking-widest mb-2">Business Development Proposal</p>
                  <h2 className="text-2xl font-bold mb-1">for {answers.business_name || answers.name || 'Your Business'}</h2>
                  <p className="text-lg text-[#39FF14] font-semibold">{results.proposal.headline}</p>
                </div>

                {/* Executive Summary */}
                <Card className="border-[#39FF14]/30">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-neutral-900 mb-2 flex items-center gap-2">
                      <div className="w-7 h-7 bg-[#39FF14]/20 rounded-lg flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#2BC40E]" /></div>
                      Executive Summary
                    </h3>
                    <p className="text-sm text-neutral-700 leading-relaxed">{results.proposal.executive_summary}</p>
                  </CardContent>
                </Card>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Target Audience */}
                  <Card className="border-neutral-200">
                    <CardContent className="p-5">
                      <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                        <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-4 h-4 text-blue-600" /></div>
                        Target Audience
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-blue-600 uppercase">Primary</p>
                          <p className="text-sm font-medium text-neutral-900">{results.proposal.target_audience?.primary}</p>
                          <ul className="mt-1 space-y-0.5">{results.proposal.target_audience?.primary_details?.map((d, i) => <li key={i} className="text-xs text-neutral-600 flex items-start gap-1"><span className="text-blue-500 mt-0.5">•</span>{d}</li>)}</ul>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-purple-600 uppercase">Secondary</p>
                          <p className="text-sm font-medium text-neutral-900">{results.proposal.target_audience?.secondary}</p>
                          <ul className="mt-1 space-y-0.5">{results.proposal.target_audience?.secondary_details?.map((d, i) => <li key={i} className="text-xs text-neutral-600 flex items-start gap-1"><span className="text-purple-500 mt-0.5">•</span>{d}</li>)}</ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Revenue Projections */}
                  <Card className="border-neutral-200 bg-gradient-to-br from-emerald-50 to-white">
                    <CardContent className="p-5">
                      <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                        <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center"><DollarSign className="w-4 h-4 text-emerald-600" /></div>
                        Revenue Projections
                      </h3>
                      <div className="space-y-2 mb-3">
                        <div className="p-2.5 bg-white rounded-lg border border-emerald-200">
                          <p className="text-[10px] text-emerald-600 uppercase font-semibold">Year 1 Conservative</p>
                          <p className="text-lg font-bold text-emerald-700">{results.proposal.revenue_projections?.year_1_conservative}</p>
                        </div>
                        <div className="p-2.5 bg-white rounded-lg border border-emerald-200">
                          <p className="text-[10px] text-emerald-600 uppercase font-semibold">Year 1 Growth</p>
                          <p className="text-lg font-bold text-emerald-700">{results.proposal.revenue_projections?.year_1_growth}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {results.proposal.revenue_projections?.key_revenue_streams?.map((s, i) => (
                          <div key={i} className="flex items-center justify-between text-xs p-1.5 bg-white/70 rounded">
                            <span className="text-neutral-700">{s.stream}</span>
                            <span className="font-semibold text-emerald-700">{s.estimate}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recommended Services */}
                <Card className="border-[#39FF14]/20">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <div className="w-7 h-7 bg-[#39FF14]/20 rounded-lg flex items-center justify-center"><Zap className="w-4 h-4 text-[#2BC40E]" /></div>
                      Recommended Services from My Biz Consulting
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {results.proposal.recommended_services?.map((s, i) => (
                        <div key={i} className="p-3 bg-[#39FF14]/5 rounded-lg border border-[#39FF14]/20">
                          <p className="text-sm font-semibold text-neutral-900 flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#2BC40E]" />{s.service}</p>
                          <p className="text-xs text-neutral-600 mt-0.5 ml-5">{s.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Implementation Timeline */}
                <Card className="border-neutral-200">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center"><Calendar className="w-4 h-4 text-amber-600" /></div>
                      Implementation Timeline
                    </h3>
                    <div className="grid md:grid-cols-3 gap-3">
                      {results.proposal.implementation_timeline?.map((phase, i) => {
                        const colors = [
                          { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", num: "bg-red-500" },
                          { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", num: "bg-amber-500" },
                          { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", num: "bg-emerald-500" },
                        ];
                        const c = colors[i] || colors[0];
                        return (
                          <div key={i} className={`${c.bg} rounded-xl p-4 border ${c.border}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-6 h-6 ${c.num} text-white rounded-full flex items-center justify-center text-xs font-bold`}>{i + 1}</span>
                              <p className={`text-sm font-bold ${c.text}`}>{phase.phase}</p>
                            </div>
                            <ul className="space-y-1">{phase.tasks?.map((t, j) => <li key={j} className="text-xs text-neutral-700 flex items-start gap-1"><span className={c.text}>•</span>{t}</li>)}</ul>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Expected Results */}
                <Card className="border-[#39FF14]/30 bg-gradient-to-r from-[#39FF14]/5 to-emerald-50">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-4 h-4 text-emerald-600" /></div>
                      Expected Results
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {results.proposal.expected_results?.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-emerald-100">
                          <CheckCircle2 className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-neutral-700">{r}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Investment & Pricing */}
                <div className="bg-[#333333] rounded-2xl p-6 text-white">
                  <p className="text-[#39FF14] text-xs font-medium uppercase tracking-widest text-center mb-4">Your Investment</p>
                  
                  {results.proposal.investment_summary ? (
                    <div className="space-y-3">
                      <div className="grid md:grid-cols-3 gap-3">
                        <div className="bg-white/10 rounded-xl p-4 text-center border border-white/10">
                          <p className="text-2xl font-bold text-[#39FF14]">$175</p>
                          <p className="text-xs text-neutral-300 mt-1">Initial Strategy Consultation</p>
                          <p className="text-[10px] text-neutral-500 mt-1">Covers Strategy, Structure, Systems, Support & Growth</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center border border-[#39FF14]/30">
                          <p className="text-2xl font-bold text-white">$500</p>
                          <p className="text-xs text-neutral-300 mt-1">Business Blueprint</p>
                          <p className="text-[10px] text-[#39FF14] mt-1">$175 consultation deducted = $325 to start</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center border border-white/10">
                          <p className="text-2xl font-bold text-white">$150-$350</p>
                          <p className="text-xs text-neutral-300 mt-1">Monthly Consulting</p>
                          <p className="text-[10px] text-neutral-500 mt-1">Based on your business needs</p>
                        </div>
                      </div>
                      
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-xs font-semibold text-[#39FF14] uppercase mb-2">What's Included Monthly</p>
                        <div className="grid grid-cols-2 gap-1">
                          {["Monthly Strategy Sessions", "Unlimited Email Support", "Opportunity Reviews", "Business Guidance", "Marketing Ideas", "Accountability", "AI Strategy & Tools", "Revenue Planning", "Launch Planning", "Partnership Guidance", "Quarterly Blueprint Updates", "Strategic Resources"].map((item, i) => (
                            <p key={i} className="text-[11px] text-neutral-300 flex items-center gap-1"><Check className="w-3 h-3 text-[#39FF14] flex-shrink-0" />{item}</p>
                          ))}
                        </div>
                      </div>

                      <div className="text-center pt-2">
                        <p className="text-xs text-neutral-400 italic">Built on Trust, Transparency, Accountability, Innovation & Legacy</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#39FF14] mb-1">{results.proposal.recommended_investment}</p>
                      <p className="text-sm text-neutral-300">All clients start with a strategy session</p>
                    </div>
                  )}
                  
                  <div className="flex justify-center gap-3 text-xs text-neutral-400 mt-4 pt-4 border-t border-white/10">
                    <span>www.mybizconsulting.net</span>
                    <span>•</span>
                    <span>215-443-1076</span>
                    <span>•</span>
                    <span>Jamilah@mybizconsulting.net</span>
                  </div>
                </div>
              </div>
            )}

            {/* Fallback: 30-Day Action Plan — always show when available */}
            {results.thirty_day_plan && (
              <div className="mb-8 space-y-4">
                <div className="bg-[#333333] rounded-2xl p-6 text-center text-white">
                  <p className="text-[#39FF14] text-xs font-medium uppercase tracking-widest mb-2">Your 30-Day Action Plan</p>
                  <h2 className="text-xl font-bold mb-1">for {answers.business_name || answers.name || 'Your Business'}</h2>
                  <p className="text-sm text-neutral-300">{results.thirty_day_plan.overview}</p>
                </div>
                <Card className="border-neutral-200">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#39FF14]" /> Implementation Timeline
                    </h3>
                    <div className="space-y-3">
                      {['week_1', 'week_2', 'week_3', 'week_4'].map((weekKey, idx) => {
                        const week = results.thirty_day_plan[weekKey];
                        if (!week) return null;
                        const colors = [
                          { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", num: "bg-red-500" },
                          { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", num: "bg-amber-500" },
                          { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", num: "bg-blue-500" },
                          { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", num: "bg-emerald-500" },
                        ];
                        const c = colors[idx];
                        return (
                          <div key={weekKey} className={`${c.bg} rounded-xl p-4 border ${c.border}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-6 h-6 ${c.num} text-white rounded-full flex items-center justify-center text-xs font-bold`}>{idx + 1}</span>
                              <p className={`text-sm font-bold ${c.text}`}>{week.title || `Week ${idx + 1}`}</p>
                            </div>
                            <p className="text-xs text-neutral-600 mb-1"><strong>Focus:</strong> {week.focus}</p>
                            <ul className="space-y-0.5 mb-1">{week.tasks?.map((t, j) => <li key={j} className="text-xs text-neutral-700 flex items-start gap-1"><span className={c.text}>•</span>{t}</li>)}</ul>
                            <p className="text-xs text-green-700 font-medium mt-1">Outcome: {week.outcome}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Download & Email Options */}
            <Card className="mb-8 border-neutral-200">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Save Your Report</h3>
                    <p className="text-sm text-neutral-500">Download or email your Business Clarity Report</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="outline"
                      onClick={handleCopyReport}
                      className="gap-2"
                      data-testid="copy-report-btn"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDownloadReport}
                      disabled={downloading}
                      className="gap-2"
                      data-testid="download-report-btn"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download
                    </Button>
                    <Button
                      onClick={handleEmailReport}
                      disabled={sendingEmail || !answers.email}
                      className="gap-2 bg-[#39FF14] hover:bg-[#32E010] text-black"
                      data-testid="email-report-btn"
                    >
                      {sendingEmail ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      Email to Me
                    </Button>
                  </div>
                </div>
                {!answers.email && (
                  <p className="text-xs text-amber-600 mt-3">
                    💡 Tip: Add your email in the assessment to receive your report via email
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Transition Message */}
            <div className="text-center mb-8 p-6 bg-neutral-100 rounded-xl">
              <p className="text-neutral-700 italic">
                The right level of support can dramatically shorten the time it takes to fix this.
              </p>
            </div>

            <div className="flex justify-center">
              <Button 
                size="lg"
                onClick={() => {
                  trackEvent('assessment_reveal_step', { step: 'see_path' });
                  setRevealStep(2);
                }}
                className="bg-neutral-900 hover:bg-neutral-800 text-white h-14 px-8"
                data-testid="see-path-btn"
              >
                See My Recommended Path
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    // Step 2: Personalized Recommendation
    if (revealStep === 2) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <Badge className="bg-[#39FF14]/10 text-[#2BC40E] border-[#39FF14]/20 mb-4">
                Personalized Recommendation
              </Badge>
              <h1 className="text-3xl font-semibold text-neutral-900 mb-4">
                Your Recommended Path Forward
              </h1>
            </div>

            {/* Recommended Program Card */}
            <Card className={`mb-6 border-2 ${recommendedPath.popular ? 'border-[#39FF14] ring-2 ring-[#39FF14]/20' : 'border-neutral-200'}`}>
              {recommendedPath.popular && (
                <div className="bg-[#39FF14] text-black text-center py-2 text-sm font-medium">
                  <Star className="w-4 h-4 inline mr-2" />
                  Most Clients Start Here
                </div>
              )}
              {recommendedPath.exclusive && (
                <div className="bg-amber-500 text-white text-center py-2 text-sm font-medium">
                  <Award className="w-4 h-4 inline mr-2" />
                  Application Only
                </div>
              )}
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#39FF14]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <PathIcon className="w-7 h-7 text-[#39FF14]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-neutral-900">{recommendedPath.title}</h2>
                    <p className="text-neutral-500">{recommendedPath.subtitle}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-3">
                    Why This Fits Your Stage
                  </h3>
                  <p className="text-neutral-700">{recommendedPath.description}</p>
                </div>

                <div className="mb-6 p-4 bg-[#39FF14]/5 rounded-lg">
                  <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-2">
                    What Success Looks Like in 90 Days
                  </h3>
                  <p className="text-neutral-800 font-medium">{recommendedPath.successLooks}</p>
                </div>

                <div className="flex justify-center">
                  <Button 
                    size="lg"
                    onClick={() => {
                      trackEvent('assessment_reveal_step', { step: 'see_pricing' });
                      setRevealStep(3);
                    }}
                    className="bg-[#39FF14] hover:bg-[#32E010] text-black font-medium h-14 px-8"
                    data-testid="see-pricing-btn"
                  >
                    See Investment Details
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
    
    // Step 3: Price Reveal with Full Details
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold text-neutral-900 mb-4">
              {recommendedPath.title}
            </h1>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              This engagement is designed to deliver clarity, momentum, and measurable progress — not just advice.
            </p>
          </div>

          {/* Pricing Card */}
          <Card className="mb-6 border-2 border-[#39FF14]/30">
            <CardContent className="p-8">
              {/* Price Display */}
              <div className="text-center mb-8 pb-6 border-b border-neutral-100">
                <p className="text-sm text-neutral-500 uppercase tracking-wider mb-2">
                  Monthly Investment
                </p>
                <p className="text-4xl font-bold text-neutral-900 mb-2">
                  {recommendedPath.price}
                </p>
                <Badge variant="outline">{recommendedPath.type}</Badge>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">
                  What&apos;s Included
                </h3>
                <ul className="space-y-3">
                  {recommendedPath.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTAs */}
              <div className="space-y-3">
                <Button 
                  size="lg"
                  onClick={() => {
                    trackEvent('assessment_cta_click', { cta: recommendedPath.cta, path: getRecommendedPath() });
                    const currentPath = getRecommendedPath();
                    if (currentPath === 'scale') {
                      navigate('/apply/scale');
                    } else {
                      navigate('/register');
                    }
                  }}
                  className="w-full bg-[#39FF14] hover:bg-[#32E010] text-black font-medium h-14"
                  data-testid="apply-btn"
                >
                  {recommendedPath.cta}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    trackEvent('assessment_cta_click', { cta: 'schedule_call' });
                    navigate('/register');
                  }}
                  className="w-full h-12"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule a Clarity Call
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Soft Safety Net */}
          <Card className="mb-8 bg-neutral-50 border-neutral-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-neutral-900 mb-3">
                Not ready for this level yet?
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    trackEvent('assessment_cta_click', { cta: 'rapid_alternative' });
                    navigate('/pricing#rapid');
                  }}
                  className="flex-1"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Start With R.A.P.I.D.™
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    trackEvent('assessment_cta_click', { cta: 'community_alternative' });
                    navigate('/community');
                  }}
                  className="flex-1"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Join Free Community
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Additional Actions */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => window.print()}>
              <Download className="w-4 h-4 mr-2" />
              Save Results
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/pricing')}
            >
              View All Programs
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                trackEvent('assessment_retake');
                setResults(null);
                setRevealStep(0);
                setCurrentSection(0);
                setAnswers({});
              }}
            >
              Retake Assessment
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading View
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Analyzing Your Business...</h2>
          <p className="text-neutral-600">Our AI is generating personalized insights for you.</p>
        </div>
      </div>
    );
  }

  // Assessment Form View
  const SectionIcon = currentSectionData?.icon || Target;

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-2">
            Business Clarity Assessment
          </h1>
          <p className="text-neutral-600">
            For entrepreneurs, entertainers & influencers — get personalized insights in just 3 minutes
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-neutral-500 mb-2">
            <span>Section {currentSection + 1} of {totalSections}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Section Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <SectionIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle>{currentSectionData?.title}</CardTitle>
                <CardDescription>
                  {currentSection === 0 && "Let's start with the basics about you and your brand"}
                  {currentSection === 1 && "Understanding your financial goals"}
                  {currentSection === 2 && "Identifying what's holding you back from the next level"}
                  {currentSection === 3 && "Envisioning your ideal future"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentSectionData?.questions.map(renderQuestion)}
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentSection === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isCurrentSectionComplete()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="assessment-next-btn"
          >
            {currentSection === totalSections - 1 ? (
              <>
                Get My Results
                <Sparkles className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Trust indicators */}
        <div className="mt-8 text-center text-sm text-neutral-500">
          <p>🔒 Your responses are private and secure</p>
          <p className="mt-1">No credit card required • Results in 3 minutes</p>
        </div>
      </div>
    </div>
  );
};

export default ClarityAssessment;
